/**
 * Onboarding org-options hook (TanStack Query).
 *
 * Sources the self-assignable role groups and locations for the onboarding
 * flow from their RESPECTIVE endpoints — the same org vocabulary the rest of
 * the app reads:
 *
 *   - roles:     GET /roles      (orgApi.listAllRoles)
 *   - locations: GET /locations  (orgApi.listAllLocations, one query per
 *                                 self-assignable kind, fanned out in parallel)
 *
 * Both lists are filtered by the `org.self_assign` guardrail from /config —
 * the SAME keys the backend enforces on the PUT /me write path (role label
 * marked self-joinable, whitelisted location kinds). The UI filter is
 * convenience, not the boundary: the server re-validates every selection
 * default-deny (see api-service-go/me_self_assign.go).
 *
 * Resilience: config-DISABLED and a genuinely EMPTY org (real data — no
 * self-assignable roles/locations configured) both return a clean, non-error
 * `data`; a real endpoint FAILURE does not — it sets `rolesFailed` /
 * `locationsFailed`, which a caller MUST branch on (a visible error + retry
 * for that step), never treat identically to "skip, nothing to show". Before
 * this distinction existed, `roles: []`/`locations: []` on error looked
 * exactly like "org has nothing configured", and onboarding/ProfileEditForm
 * silently skipped the step or downgraded the picker instead of surfacing the
 * failure — a nurse's ward pick then never became the ACL-bound reference it
 * should have. `retry: 1` gives a single cold-start retry before a failure is
 * reported; beyond that it is a real, reportable failure, not silently eaten.
 * The two queries still degrade INDEPENDENTLY — a failed roles fetch does not
 * block the location step, and vice versa.
 */

import { useMemo } from 'react';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { useConfigStore } from '@/stores/config';
import { useSelfAssign } from '@/components/admin/org/useOrg';
import { orgApi, type Location, type Role } from '@/api/endpoints/org';
import { QUERY_STALE_TIMES } from '@/lib/query-config';

export interface OnboardingOrgOptions {
  enabled: boolean;
  roles: Role[];
  /** True when self-assign is enabled but the ROLE fetch itself failed
   *  (beyond the single cold-start retry) — distinct from `roles: []` meaning
   *  "the org genuinely has none configured". Callers must show a visible
   *  error + retry for the role step when this is true, never skip silently. */
  rolesFailed: boolean;
  locations: Location[];
  /** Same distinction as rolesFailed, for the LOCATION fetch. */
  locationsFailed: boolean;
}

export function useOnboardingOrgOptions(): {
  data: OnboardingOrgOptions | undefined;
  isLoading: boolean;
  /** Retry the roles/locations fetch after a rolesFailed/locationsFailed —
   *  wired directly to each query's own refetch, so a retry re-runs only the
   *  step that actually failed. */
  retryRoles: () => void;
  retryLocations: () => void;
} {
  // /config gates everything: until it settles we must report "loading" so the
  // state machine never auto-skips an org step that is about to become
  // available. A fallback config carries no org block → self-assign disabled.
  const isConfigLoaded = useConfigStore((s) => s.isConfigLoaded);
  const selfAssign = useSelfAssign();
  const enabled = selfAssign?.enabled === true;
  const roleLabel = selfAssign?.role_label ?? '';
  const locationKinds = selfAssign?.location_kinds;

  const rolesQuery = useAuthenticatedQuery<Role[]>({
    queryKey: ['onboarding', 'org', 'roles'],
    queryFn: () => orgApi.listAllRoles(),
    // role_label is guaranteed non-empty whenever self-assign is enabled — the
    // backend fails boot otherwise (OrgConfig.Validate). isConfigLoaded is
    // implied by `enabled` (self_assign comes from config) but stated
    // explicitly: orgPageSize() requires loaded config, same as the admin
    // hooks in useOrg.
    enabled: isConfigLoaded && enabled,
    // One retry covers a cold api-service-go; beyond that we degrade to "skip"
    // rather than spin, so onboarding stays completable.
    retry: 1,
    // Org vocabulary changes about as often as the user directory (an admin
    // edits the org tree) — reuse the config-driven directory stale time, same
    // precedent as useMedicationSearch.
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  });

  const locationsQuery = useAuthenticatedQuery<Location[]>({
    queryKey: ['onboarding', 'org', 'locations', locationKinds],
    queryFn: async () => {
      // One full-set fetch per self-assignable kind, in parallel; flatten in
      // config order so the picker is stable across loads.
      const perKind = await Promise.all(
        (locationKinds ?? []).map((kind) => orgApi.listAllLocations({ kind })),
      );
      return perKind.flat();
    },
    enabled: isConfigLoaded && enabled && (locationKinds?.length ?? 0) > 0,
    retry: 1,
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  });

  const rolesData = rolesQuery.data;
  const locationsData = locationsQuery.data;

  const data = useMemo<OnboardingOrgOptions | undefined>(() => {
    if (!isConfigLoaded) return undefined;
    if (!enabled) return { enabled: false, roles: [], locations: [], rolesFailed: false, locationsFailed: false };
    // Standard TanStack contract: data stays undefined until the initial
    // fetches settle, so a consumer that forgets the isLoading guard can never
    // mistake "still loading" for "enabled but empty" and skip an org step.
    if (rolesQuery.isLoading || locationsQuery.isLoading) return undefined;
    return {
      enabled: true,
      // Offer only roles an operator tagged self-joinable — the same labels
      // key the backend checks on write. On a genuine fetch failure there is
      // no real list to filter — [] plus rolesFailed:true, not a silent
      // "org has none configured".
      roles: rolesQuery.isError ? [] : (rolesData ?? []).filter((r) => r.labels[roleLabel] === 'true'),
      rolesFailed: rolesQuery.isError,
      locations: locationsQuery.isError ? [] : (locationsData ?? []),
      locationsFailed: locationsQuery.isError,
    };
  }, [isConfigLoaded, enabled, roleLabel, rolesData, locationsData, rolesQuery.isLoading, locationsQuery.isLoading, rolesQuery.isError, locationsQuery.isError]);

  return {
    data,
    isLoading: !isConfigLoaded || rolesQuery.isLoading || locationsQuery.isLoading,
    retryRoles: () => void rolesQuery.refetch(),
    retryLocations: () => void locationsQuery.refetch(),
  };
}
